import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { Button } from '@savvy-kit/components/button';
import { Text } from '@savvy-kit/foundations/text';
import { isNil } from 'es-toolkit';
import { Form, FormikProvider, useFormik } from 'formik';
import { graphql } from 'src/gql';
import { object as yupObject, string as yupString } from 'yup';

import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { XSavvyFooter } from '@savvy/deprecated-shared/x-components/x-savvy-footer';
import { XSavvyLogoHeader } from '@savvy/deprecated-shared/x-components/x-savvy-logo-header';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import defaultGuard from '@savvy/utils/default-guard';

import { FormikInputField } from '../formik-input-field';
import { FormikSearchableCombobox } from '../inputs/formik-searchable-combobox';
import { TrackedButton } from '../tracked';
import { CheckYourEmail } from './check-your-email';

const SignUpForSavvyMutation = graphql(/* GraphQL */ `
  mutation SignUpForSavvy($input: SignUpForSavvyInput!) {
    signUpForSavvy(input: $input) {
      softError
    }
  }
`);

const UNEXPECTED_ERROR_MSG = 'An unexpected error occurred';
type LeadSource = 'Centers of Influence' | 'Referral' | 'Web search' | 'Other';

/** @all */
const leadSourceOptions: LeadSource[] = ['Centers of Influence', 'Referral', 'Web search', 'Other'] as const;

type CreateAccountFormik = {
  firstName: string;
  lastName: string;
  email: string;
  advisorLeadSource: LeadSource | null;
  otherLeadSource: string | null;
  referralSourceEmail: string | null;
};

export function CreateAccount() {
  const isSmallScreen = useIsSmallScreen();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [signedUpSuccessfully, setSignedUpSuccessfully] = useState(false);
  const [signUpForSavvy] = useMutationWithErrorLogging(SignUpForSavvyMutation);
  const router = useRouter();
  const utmCampaign_raw = router.query['utm_campaign'];
  const utmCampaign = !isNil(utmCampaign_raw) ? utmCampaign_raw.toString() : null;
  const shouldSurfaceHowDidYouHearAboutUs = isNil(utmCampaign);

  useEffect(() => {
    if (utmCampaign) {
      formik.setFieldValue('advisorLeadSource', 'Centers of Influence');
    }
  }, [utmCampaign]);

  const formik = useFormik<CreateAccountFormik>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      advisorLeadSource: utmCampaign ? 'Centers of Influence' : null,
      referralSourceEmail: null,
      otherLeadSource: null,
    },
    validationSchema: yupObject({
      firstName: yupString().required('First name is required'),
      lastName: yupString().required('Last name is required'),
      email: yupString().email('Please enter a valid email address').required('Email is required'),
      advisorLeadSource: yupString().nullable().required('This field is required'),
      otherLeadSource: yupString()
        .nullable()
        .when('advisorLeadSource', {
          is: (advisorLeadSource: LeadSource | null) => advisorLeadSource === 'Other',
          then: yupString().nullable().required('This field is required'),
          otherwise: yupString().nullable(),
        }),
      referralSourceEmail: yupString()
        .nullable()
        .when('advisorLeadSource', {
          is: (advisorLeadSource: LeadSource | null) => advisorLeadSource === 'Referral',
          then: yupString()
            .email('Please enter a valid email address')
            .nullable()
            .required('Please enter the email address of the person who referred you'),
          otherwise: yupString().nullable(),
        }),
    }),
    onSubmit: () => {
      setSubmissionError(null);
      setIsSubmitting(true);
      signUpForSavvy({
        variables: {
          input: {
            ...formik.values,
            advisorLeadSource: mapLeadSourceToSalesforceLeadSource(formik.values.advisorLeadSource),
            acceptedTos: true,
            utmCampaign: utmCampaign ?? null,
          },
        },
      })
        .then((result) => {
          const softError = result.data?.signUpForSavvy?.softError;
          console.error(softError);
          if (softError) {
            console.error(softError);
            switch (softError) {
              case 'unexpected_error':
                setSubmissionError(UNEXPECTED_ERROR_MSG);
                break;
              case 'user_must_accept_tos':
                setSubmissionError('You must accept the terms of service to sign up');
                break;
              default:
                setSubmissionError(defaultGuard(softError, UNEXPECTED_ERROR_MSG));
                break;
            }
          } else {
            setSignedUpSuccessfully(true);
          }
        })
        .catch((err: unknown) => {
          console.error(err);
          setSubmissionError(UNEXPECTED_ERROR_MSG);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
  });

  if (signedUpSuccessfully) {
    return <CheckYourEmail email={formik.values.email} />;
  }

  return (
    <FormikProvider value={formik}>
      <Form noValidate onSubmit={formik.handleSubmit}>
        <div className="flex flex-col min-h-screen p-8">
          <XSavvyLogoHeader />

          <main className="mx-auto w-full max-w-[600px] px-4 sm:px-6 grow py-8 flex items-center">
            <div className="flex flex-col pb-16 w-full gap-4">
              <Text variant={isSmallScreen ? 'h4' : 'h3'} color="dark" className="pb-2">
                Sign up for Savvy
              </Text>
              <Text variant={isSmallScreen ? 'body-sm' : 'body'} color="secondary" className="mb-6">
                {`Already have a Savvy account? `}
                <Button variant="link" className="h-auto p-0 align-baseline" render={<NextLink href="/login" />}>
                  Log in instead
                </Button>
              </Text>
              <FormikInputField
                id="firstName"
                autoComplete="given-name"
                autoFocus={!isSmallScreen}
                label="First Name"
                name="firstName"
                type="text"
              />
              <FormikInputField
                id="lastName"
                autoComplete="family-name"
                label="Last Name"
                name="lastName"
                type="text"
              />
              <FormikInputField id="email" autoComplete="email" label="Email Address" name="email" type="email" />
              {shouldSurfaceHowDidYouHearAboutUs && (
                <>
                  <FormikSearchableCombobox
                    label="How did you hear about us?"
                    name="advisorLeadSource"
                    options={leadSourceOptions}
                  />
                  {formik.values.advisorLeadSource === 'Referral' && (
                    <FormikInputField
                      id="referralSourceEmail"
                      label="Referral source email"
                      name="referralSourceEmail"
                      type="email"
                    />
                  )}
                  {formik.values.advisorLeadSource === 'Other' && (
                    <FormikInputField id="otherLeadSource" label="Please specify" name="otherLeadSource" type="text" />
                  )}
                </>
              )}
              {submissionError && (
                <Text variant="caption" color="destructive">
                  {submissionError}
                </Text>
              )}
              <TrackedButton
                elementName="create-savvy-account-sign-up-button"
                disabled={isSubmitting || !isNil(formik.errors.email)}
                isLoading={isSubmitting}
                size="lg"
                type="submit"
                className="mt-4 w-full"
              >
                Sign up
              </TrackedButton>
              <Text variant="caption" color="secondary" className="mt-2">
                By signing up, you agree to our{' '}
                <Button
                  variant="link"
                  className="h-auto p-0 align-baseline"
                  render={<a href="http://www.savvywealth.com/savvywealthterms" target="_blank" rel="noreferrer" />}
                >
                  Terms of Service
                </Button>{' '}
                and{' '}
                <Button
                  variant="link"
                  className="h-auto p-0 align-baseline"
                  render={<a href="http://www.savvywealth.com/savvywealthprivacy" target="_blank" rel="noreferrer" />}
                >
                  Privacy Policy
                </Button>
              </Text>
            </div>
          </main>
          <XSavvyFooter />
        </div>
      </Form>
    </FormikProvider>
  );
}

function mapLeadSourceToSalesforceLeadSource(advisorLeadSource: LeadSource | null): string | null {
  switch (advisorLeadSource) {
    case 'Centers of Influence':
      return 'Centers of Influence';
    case 'Referral':
      return 'Client referral';
    case null:
      return null;
    case 'Web search':
      return 'Web search';
    case 'Other':
      return 'Other';
  }
}
